import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { withLoading } from "hocs/withLoading.hoc";
import { withUserLoading } from "hocs/withUserLoading.hoc";
import LoginPage from "pages/LoginPage";
import MainLayout from "layouts/MainLayout/MainLayout";
import RequireAuth from "./RequireAuth";
import ProjectHeaderLayouts from "components/Projects/ProjectHeaderLayouts";
import SignUpPage from "pages/SignUpPage";
import ProjectScreeningHeaderLayouts from "components/ScreeningCandidate/ProjectScreeningHeaderLayouts";
import ResetPasswordPage from "pages/ResetPasswordPage";
import ForgotPasswordPage from "pages/ForgotPasswordPage";
import SubscriptionPlans from "components/UpgradePlan/SubscriptionPlan";
import SubscriptionPage from "pages/SubscriptionPage";
import { readUser } from "services/localStorage.service";
import RestrictedRoute from "./RestrictedRoute";
import { AfterSignUp } from "components/SignUp/AfterSignUp";
import AllSearchedCandidatesPage from "pages/AllSearchedCandidatesPage";
import { useIsOldFlow } from "services/CustomeHooks";

export const DASHBOARD_PATH = "/";

const AuthLayoutFallback = React.lazy(() => import("layouts/AuthLayout/AuthLayout"));
const CandidateScreeningFormPage = React.lazy(() => import("pages/CandidateScreeningFormPage"));
const CandidateScreeningPage = React.lazy(() => import("pages/CandidateScreeningPage"));
const CustomScreeningInterviewPage = React.lazy(() => import("pages/CustomScreeningInterviewPage"));
const CandidateScreeningEventCalenderPage = React.lazy(
  () => import("pages/CandidateScreeningEventCalenderPage")
);
const EditCandidateProfilePage = React.lazy(() => import("pages/EditCandidateProfilePage"));
const CandidateDetailsPage = React.lazy(() => import("pages/CandidateDetailsPage"));
const CandidateScreeningResultPage = React.lazy(() => import("pages/CandidateScreeningResultPage"));
const ProjectsPage = React.lazy(() => import("pages/ProjectsPage"));
const CreateProjectPage = React.lazy(() => import("pages/CreateProjectPage"));
const DashboardPage = React.lazy(() => import("pages/DashboardPage"));
const Logout = React.lazy(() => import("./Logout"));
const ScreeningCandidateLayoutPage = React.lazy(
  () => import("../components/ScreeningCandidate/ScreeningCandidateLayout")
);
const MailLayoutComponent = React.lazy(() => import("../components/MailComponents/MailLayout"));
const ScreeningCandidatePage = React.lazy(() => import("pages/ScreeningCandidatePage"));
const SearchForProjectPage = React.lazy(() => import("pages/SearchSelectorPage"));
const SearchResultsPage = React.lazy(() => import("pages/SearchResultPage"));
const TalentPoolsPage = React.lazy(() => import("pages/TalentPoolsPage"));
const RecruiterScreeningPage = React.lazy(() => import("pages/RecruiterPage"));
const RecruiterCalendarScreeningPage = React.lazy(() => import("pages/RecruiterCalendarPage"));
const RecruitersCandidateScreeningPage = React.lazy(() => import("pages/RecruitersCandidatePage"));
const CandidateFoldersScreeningPage = React.lazy(
  () => import("pages/CandidateFoldersScreeningPage")
);
const ReportEditorPage = React.lazy(() => import("pages/ReportEditorPage"));
const CreateProjectScreeningPage = React.lazy(() => import("pages/CreateProjectScreeningPage"));
const ProjectSavedSearchesScreeningPage = React.lazy(
  () => import("pages/ProjectSavedSearchesScreeningPage")
);
const ProjectRecentSearchesScreeningPage = React.lazy(
  () => import("pages/ProjectRecentSearchesScreeningPage")
);
const ProjectWiseSavedSearchesScreeningPage = React.lazy(
  () => import("pages/ProjectWiseSavedSearchesScreeningPage")
);
const ProjectWiseRecentSearchesScreeningPage = React.lazy(
  () => import("pages/ProjectWiseRecentSearchesScreeningPage")
);
const ProjectDetailsScreeningPage = React.lazy(() => import("pages/ProjectDetailsScreeningPage"));
const ProjectTalentPoolScreeningPage = React.lazy(
  () => import("pages/ProjectTalentPoolScreeningPage")
);
const ProjectAnalyticsScreeningPage = React.lazy(
  () => import("pages/ProjectAnalyticsScreeningPage")
);
const ReportsListPage = React.lazy(() => import("pages/ReportsListPage"));
const SortCandidatesPage = React.lazy(() => import("pages/SortCandidatesPage"));
const ReportsListMainPage = React.lazy(() => import("pages/ReportsListMainPage"));

const ProjectScreeningPage = React.lazy(() => import("pages/ProjectScreeningPage"));

const RecruiterAIAssistantCandidateScreeningPage = React.lazy(
  () => import("pages/RecruiterAIAssistantCandidateScreeningPage")
);
const SettingsPage = React.lazy(() => import("pages/SettingsPage"));
const ResourceCenterPage = React.lazy(() => import("pages/ResourceCenterPage"));
const ScreeningQuestionPage = React.lazy(() => import("pages/ScreeningQuestionPage"));
const IntegrationsPage = React.lazy(() => import("pages/IntegrationsPage"));
const UpgradePlanPage = React.lazy(() => import("pages/UpgradePlanPage"));
const AnalyticsPage = React.lazy(() => import("pages/AnalyticsPage"));
const MailPage = React.lazy(() => import("pages/MailPage"));
const ViewMailPage = React.lazy(() => import("pages/ViewMailPage"));
const BulkMailPage = React.lazy(() => import("pages/BulkMailPage"));
const CompanyGroupListPage = React.lazy(() => import("pages/CompanyGroupListPage"));
const CandidateCriteriaPage = React.lazy(() => import("pages/CandidateCriteriaPage"));
const QuestionSetPage = React.lazy(() => import("pages/QuestionSetPage"));
const ScreeningListPage = React.lazy(() => import("pages/ScreeningListPage"));
const UserProjectListPage = React.lazy(() => import("pages/UserProjectListPage"));
const UserProjectDetails = React.lazy(() => import("components/UserProjects/UserProjectDetails"));

const IntegrationResponsePage = React.lazy(() => import("pages/IntegrationResponsePage"));
const IntegrationResponseOutlookPage = React.lazy(
  () => import("pages/IntegrationResponseOutlookPage")
);
const PrivacyPolicyPage = React.lazy(() => import("pages/PrivacyPolicyPage"));
const TermsAndConditionsPage = React.lazy(() => import("pages/TermsAndConditionsPage"));
const HRFlowPage = React.lazy(() => import("pages/HRFlowPage"));
const HomeScreenPage = React.lazy(() => import("pages/HomeScreenPage"));
const EmailResolvePage = React.lazy(() => import("pages/EmailResolvePage"));
const ExecutiveSearchPage = React.lazy(() => import("pages/ExecutiveSearchPage"));

const CandidateScreening = withLoading(CandidateScreeningPage);
const CustomScreeningInterview = withLoading(CustomScreeningInterviewPage);
const CandidateScreeningEventCalender = withLoading(CandidateScreeningEventCalenderPage);
const EditCandidateProfile = withLoading(EditCandidateProfilePage);
const CandidateDetails = withLoading(CandidateDetailsPage);
const CandidateScreeningResult = withLoading(CandidateScreeningResultPage);
const Projects = withLoading(ProjectsPage);
const CreateProject = withLoading(CreateProjectPage);
const Dashboard = withUserLoading(DashboardPage);
const LogoutFallback = withLoading(Logout);
const ScreeningCandidateLayout = withLoading(ScreeningCandidateLayoutPage);
const ScreeningCandidate = withLoading(ScreeningCandidatePage);
const ScreeningCandidateForm = withLoading(CandidateScreeningFormPage);
const SearchForProject = withLoading(SearchForProjectPage);
const SearchResult = withLoading(SearchResultsPage);
const TalentPools = withLoading(TalentPoolsPage);
const RecruiterScreening = withLoading(RecruiterScreeningPage);
const RecruiterCalendarScreening = withLoading(RecruiterCalendarScreeningPage);
const RecruitersCandidateScreening = withLoading(RecruitersCandidateScreeningPage);
const CandidateFoldersScreening = withLoading(CandidateFoldersScreeningPage);
const ReportEditor = withLoading(ReportEditorPage);
const CreateProjectScreening = withLoading(CreateProjectScreeningPage);
const ProjectSavedSearchesScreening = withLoading(ProjectSavedSearchesScreeningPage);
const ProjectRecentSearchesScreening = withLoading(ProjectRecentSearchesScreeningPage);
const ProjectWiseSavedSearchesScreening = withLoading(ProjectWiseSavedSearchesScreeningPage);
const ProjectWiseRecentSearchesScreening = withLoading(ProjectWiseRecentSearchesScreeningPage);
const ProjectDetailsScreening = withLoading(ProjectDetailsScreeningPage);
const ProjectTalentPoolScreening = withLoading(ProjectTalentPoolScreeningPage);
const ProjectAnalyticsScreening = withLoading(ProjectAnalyticsScreeningPage);
const ReportsList = withLoading(ReportsListPage);
const SortCandidates = withLoading(SortCandidatesPage);
const ReportsListMain = withLoading(ReportsListMainPage);
const ProjectScreening = withLoading(ProjectScreeningPage);
const IntegrationResponse = withLoading(IntegrationResponsePage);
const IntegrationResponseOutlook = withLoading(IntegrationResponseOutlookPage);
const RecruiterAIAssistantCandidateScreening = withLoading(
  RecruiterAIAssistantCandidateScreeningPage
);
const Settings = withLoading(SettingsPage);
const ResourceCenter = withLoading(ResourceCenterPage);
const ScreeningQuestion = withLoading(ScreeningQuestionPage);
const Integrations = withLoading(IntegrationsPage);
const CompanyGroupList = withLoading(CompanyGroupListPage);
const CandidateCriteria = withLoading(CandidateCriteriaPage);
const QuestionSet = withLoading(QuestionSetPage);
const UpgradePlan = withLoading(UpgradePlanPage);
const Analytics = withLoading(AnalyticsPage);
const MailLayout = withLoading(MailLayoutComponent);
const Mail = withLoading(MailPage);
const ViewMail = withLoading(ViewMailPage);
const BulkMail = withLoading(BulkMailPage);
const ScreeningList = withLoading(ScreeningListPage);
const UserProjectList = withLoading(UserProjectListPage);
const UserProject = withLoading(UserProjectDetails);
const PrivacyPolicy = withLoading(PrivacyPolicyPage);
const TermsAndConditions = withLoading(TermsAndConditionsPage);
const HRFlow = withLoading(HRFlowPage);
const HomeScreen = withUserLoading(HomeScreenPage);

const ExecutiveSearch = withLoading(ExecutiveSearchPage);
const EmailResolve = withLoading(EmailResolvePage);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const AppRouter: React.FC = () => {
  const protectedLayout = (
    <RequireAuth>
      <MainLayout />
    </RequireAuth>
  );
  ///app/v1/explorator/search-stats/?search_id=3709
  //app/v1/explorator/search-stats/?search_id=23172
  const getUserInfo = readUser();
  const isOldSearchFlow = useIsOldFlow();

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/thank-you" element={<AfterSignUp />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route
          path={DASHBOARD_PATH}
          element={
            <RestrictedRoute
              user={getUserInfo}
              allowedRoles={["admin", "RECRUITER", "ADMIN", "SALES_REP"]}
            >
              {protectedLayout}
            </RestrictedRoute>
          }
        >
          {/* <Route index element={<Dashboard />} /> */}
          {/* <Route path="/search-query" element={<QuestionSearchResult />} /> */}
          <Route path="/screening" element={<ScreeningList />} />
          <Route path="/screening/question-set" element={<ScreeningQuestion />} />
          {/* <Route path="/terms-and-conditions" element={<TermsAndConditions />} /> */}
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="/hr-flow" element={<HRFlow />} />
          <Route path="/hr-flow/search" element={<HRFlow />} />
          <Route index element={isOldSearchFlow ? <Dashboard /> : <HomeScreen />} />
          <Route
            path="/hr-flow/search/search-result/:searchId/:gSearchId"
            element={<AllSearchedCandidatesPage />}
          />
          <Route path="projects" element={<Projects />} />
          <Route
            path="projects/saved-searches/:id"
            element={
              <ProjectHeaderLayouts>
                <ProjectSavedSearchesScreening />
              </ProjectHeaderLayouts>
            }
          />
          <Route
            path="projects/recent-searches/:id"
            element={
              <ProjectHeaderLayouts>
                <ProjectRecentSearchesScreening />
              </ProjectHeaderLayouts>
            }
          />
          <Route
            path="projects/wise/saved-searches/:id"
            element={
              <ProjectHeaderLayouts>
                <ProjectWiseSavedSearchesScreening />
              </ProjectHeaderLayouts>
            }
          />
          <Route
            path="projects/wise/saved-searches/executive-search/:id"
            element={
              <ProjectHeaderLayouts>
                <ExecutiveSearch />
              </ProjectHeaderLayouts>
            }
          />
          <Route
            path="projects/wise/recent-searches/:id"
            element={
              <ProjectHeaderLayouts>
                <ProjectWiseRecentSearchesScreening />
              </ProjectHeaderLayouts>
            }
          />
          <Route
            path="projects/details/:id"
            element={
              <ProjectHeaderLayouts>
                <ProjectDetailsScreening />
              </ProjectHeaderLayouts>
            }
          />
          <Route
            path="projects/talent-pool/:id"
            element={
              <ProjectHeaderLayouts>
                <ProjectTalentPoolScreening />
              </ProjectHeaderLayouts>
            }
          />
          <Route
            path="projects/analytics/:id"
            element={
              <ProjectHeaderLayouts>
                <ProjectAnalyticsScreening />
              </ProjectHeaderLayouts>
            }
          />

          <Route path="edit/candidate/:id" element={<EditCandidateProfile />} />
          <Route path="candidate-details/:id" element={<CandidateDetails />} />
          <Route
            path="projects/screening/:id"
            element={
              <ProjectScreeningHeaderLayouts>
                <ProjectScreening searchQuery="defaultSearchQuery" />
              </ProjectScreeningHeaderLayouts>
            }
          />

          <Route path="mail" element={<MailLayout />}>
            <Route index element={<Navigate to="inbox" />} />
            <Route path="inbox" element={<Mail />} />
            <Route path="inbox/:threadId" element={<ViewMail />} />
            <Route path="bulk-mail/:projectId" element={<BulkMail />} />
          </Route>

          {/* <Route path="projects">
            <Route index element={<Projects />} />
            <Route path="role-details/:id" element={<RoleDetails />} /> 
          </Route> */}
          <Route path="create-project" element={<CreateProject />} />
          <Route path="search-for-project" element={<SearchForProject />} />
          <Route path="search/search-result/:searchId/:gSearchId" element={<SearchResult />} />

          <Route path="talent-pools" element={<TalentPools />} />
          <Route
            path="search/search-result/candidate-screening"
            element={<ScreeningCandidateLayout />}
          >
            <Route index element={<Navigate to="screening-candidates" />} />
            <Route path="screening-candidates" element={<ScreeningCandidate />} />
            <Route path="event-calender" element={<CandidateScreeningEventCalender />} />
            <Route path="add-candidate-screening-form" element={<ScreeningCandidateForm />} />
            <Route path="screening-candidates/:id" element={<CandidateScreeningResult />} />
          </Route>
          <Route path="integrations" element={<Integrations />} />
          {/* <Route path="upgrade-plan" element={<UpgradePlan />} /> */}

          <Route path="analytics" element={<Analytics />} />
          <Route path="/company-group">
            <Route index path="list" element={<CompanyGroupList />} />
            {/* <Route path="role-details/:id" element={<RoleDetails />} /> */}
          </Route>
          <Route path="/candidate-criteria" element={<CandidateCriteria />} />
          <Route path="/candidate-screening">
            <Route index path="question-templates" element={<QuestionSetPage />} />
            {/* <Route path="role-details/:id" element={<RoleDetails />} /> */}
          </Route>
          <Route path="recruiters/candidate/folder/list" element={<CandidateFoldersScreening />} />

          <Route path="/search">
            <Route index element={<Projects />} />
            <Route path="name-search" element={<CompanyGroupList />} />
            <Route path="saved-search" element={<ProjectSavedSearchesScreening />} />
            <Route path="recent-search" element={<ProjectRecentSearchesScreening />} />
          </Route>
          <Route path="/integrations">
            <Route path="list" element={<Integrations />} />
            <Route path="integration-response" element={<IntegrationResponse />} />
            <Route path="integration-response-outlook" element={<IntegrationResponseOutlook />} />
          </Route>

          <Route path="subscription" element={<SubscriptionPage />} />
          <Route path="settings" element={<Settings />} />
          <Route path="email-resolve" element={<EmailResolve />} />
        </Route>

        <Route path={DASHBOARD_PATH} element={protectedLayout}>
          <Route path="user-project-list" element={<MailLayout />}>
            {/* <Route index element={<Navigate to="inbox" />} /> */}
            <Route index element={<UserProjectList />} />
            <Route path=":projectId" element={<UserProject />} />
          </Route>
          <Route path="resource-center" element={<ResourceCenter />} />
          <Route path="upgrade-plan" element={<SubscriptionPage />} />
          <Route
            path="projects/report-list/:id"
            element={
              <ProjectHeaderLayouts>
                <ReportsList />
              </ProjectHeaderLayouts>
            }
          />
          <Route path="/report-editor" element={<MailLayout />}>
            <Route index element={<ReportEditor />} />
          </Route>
          <Route path="report-list" element={<MailLayout />}>
            <Route path="" element={<ReportsListMain />} />
          </Route>
          <Route path="projects/sort-candidates/:id" element={<MailLayout />}>
            <Route path="" element={<SortCandidates />} />
          </Route>
        </Route>
        <Route path="/auth" element={<AuthLayoutFallback />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password/:uid/:token" element={<ResetPasswordPage />} />
          <Route path="signup" element={<SignUpPage />} />
          <Route path="pricing" element={<SubscriptionPlans />} />
        </Route>
        <Route path="/logout" element={<LogoutFallback />} />
        <Route path="recruiters" element={<RecruiterScreening />} />
        <Route path="recruiters-calendar" element={<RecruiterCalendarScreening />} />
        <Route path="recruiters-candidate" element={<RecruitersCandidateScreening />} />

        <Route
          path="recruiters-candidate-ai-assistant"
          element={<RecruiterAIAssistantCandidateScreening />}
        />

        <Route
          path="recruiters-candidate-ai-assistant"
          element={<RecruiterAIAssistantCandidateScreening />}
        />
        <Route path="/interview">
          <Route path="automated-interview/:token" element={<CandidateScreening />} />
          <Route path="text-interview/:token" element={<CustomScreeningInterview />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};
